// src/pages/stock/ProductHistory.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { exportStyledExcel, QTY_FMT } from '../../utils/exportExcel';

const GREEN    = '#2e9f86';
const PAGE_SIZE = 50;

const DOC_TYPE_LABELS = {
  100: { label: 'Purchase In',     bg: '#eff6ff', color: '#1e40af' },
  120: { label: 'Purchase Return', bg: '#fff7ed', color: '#9a3412' },
  200: { label: 'Sales Out',       bg: '#f0fdf4', color: '#166534' },
  220: { label: 'Sales Return',    bg: '#f5f3ff', color: '#5b21b6' },
  230: { label: 'Proforma',        bg: '#f3f4f6', color: '#374151' },
  300: { label: 'Inventory Count', bg: '#f0f9ff', color: '#075985' },
  400: { label: 'Loss / Damage',   bg: '#fef2f2', color: '#991b1b' },
};

const pad = (x) => String(x).padStart(2, '0');
const fmtLocal = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const asArray = (d) =>
  Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];

export default function ProductHistory() {
  const navigate = useNavigate();
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productFilter, setProductFilter] = useState('');
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(1);

  // fetch all products for the picker
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get('/api/products?all=true').then(r => asArray(r.data)),
  });

  // fetch movements for selected product
  const { data: movements = [], isLoading, refetch } = useQuery({
    queryKey: ['product-history', selectedProductId],
    queryFn: () => api.get(`/api/stock-movements/product/${selectedProductId}`).then(r => asArray(r.data)),
    enabled: !!selectedProductId,
  });

  // narrow the picker by name / SKU / barcode (type or scan)
  const shownProducts = useMemo(() => {
    const q = productFilter.trim().toLowerCase();
    if (!q) return products;
    return products.filter(p =>
      p.name?.toLowerCase().includes(q) ||
      String(p.sku || '').toLowerCase().includes(q) ||
      String(p.barcode || '').toLowerCase().includes(q)
    );
  }, [products, productFilter]);

  // exact barcode scan → select the product immediately
  useEffect(() => {
    const q = productFilter.trim();
    if (!q) return;
    const hit = products.find(p => String(p.barcode || '') === q);
    if (hit && hit._id !== selectedProductId) {
      setSelectedProductId(hit._id);
      setSearch('');
      setPage(1);
    }
  }, [productFilter, products]); // eslint-disable-line

  const selectedProduct = useMemo(
    () => products.find(p => p._id === selectedProductId) || null,
    [products, selectedProductId]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return movements;
    const q = search.toLowerCase();
    return movements.filter(m =>
      m.document_number?.toLowerCase().includes(q) ||
      m.account_name?.toLowerCase().includes(q) ||
      m.variant_name?.toLowerCase().includes(q)
    );
  }, [movements, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // summary stats
  const stats = useMemo(() => {
    const totalIn  = movements.filter(m => (m.quantity_change ?? 0) > 0).reduce((s, m) => s + (m.quantity_change ?? 0), 0);
    const totalOut = movements.filter(m => (m.quantity_change ?? 0) < 0).reduce((s, m) => s + Math.abs(m.quantity_change ?? 0), 0);
    const current  = selectedProduct?.stock ?? (movements[0]?.quantity_after ?? '—');
    return { totalIn, totalOut, current, count: movements.length };
  }, [movements, selectedProduct]);

  return (
    <div>
      {/* Product picker */}
      <div className="flex flex-wrap gap-3 mb-5 items-center">
        <input
          type="text"
          placeholder="Find product by name, SKU or barcode…"
          value={productFilter}
          onChange={e => setProductFilter(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
          style={{ minWidth: 230 }}
        />
        <select
          value={selectedProductId}
          onChange={e => { setSelectedProductId(e.target.value); setSearch(''); setPage(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
          style={{ minWidth: 280 }}
        >
          <option value="">— Select a product ({shownProducts.length}) —</option>
          {shownProducts.map(p => (
            <option key={p._id} value={p._id}>
              {p.name}{p.sku ? ` (${p.sku})` : ''}
            </option>
          ))}
        </select>

        {selectedProductId && (
          <>
            <input
              type="text"
              placeholder="Search doc#, account, variant…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
              style={{ minWidth: 220 }}
            />
            <button
              onClick={() => refetch()}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              ↻ Refresh
            </button>
            <button
              onClick={() => {
                const typeLabel = (code) => (DOC_TYPE_LABELS[code]?.label || String(code));
                exportStyledExcel({
                  filename: `history_${(selectedProduct?.name || 'product').replace(/[^\w]+/g, '_')}`,
                  sheetName: 'History',
                  title: `Product History — ${selectedProduct?.name || ''}`,
                  subtitle: `Current stock: ${stats.current} · In: +${stats.totalIn} · Out: -${stats.totalOut} · ${filtered.length} movements`,
                  columns: [
                    { header: 'Date', key: 'date', width: 13 },
                    { header: 'Type', key: 'type', width: 16 },
                    { header: 'Document', key: 'doc', width: 14 },
                    { header: 'Account', key: 'account', width: 22 },
                    { header: 'Variant', key: 'variant', width: 14 },
                    { header: 'Change', key: 'change', width: 10, numFmt: QTY_FMT, align: 'right' },
                    { header: 'Before', key: 'before', width: 10, numFmt: QTY_FMT, align: 'right' },
                    { header: 'After', key: 'after', width: 10, numFmt: QTY_FMT, align: 'right' },
                  ],
                  rows: filtered.map(m => ({
                    date: m.createdAt ? fmtLocal(m.createdAt) : '',
                    type: typeLabel(m.document_type),
                    doc: m.document_number || '',
                    account: m.account_name || '',
                    variant: m.variant_name || '',
                    change: Number(m.quantity_change ?? 0),
                    before: m.quantity_before ?? '',
                    after: m.quantity_after ?? '',
                  })),
                });
              }}
              className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg"
            >
              Export Excel
            </button>
            <button
              onClick={() => {
                const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;');
                const typeLabel = (code) => (DOC_TYPE_LABELS[code]?.label || String(code));
                const rows = filtered.map(m => {
                  const ch = m.quantity_change ?? 0;
                  return `<tr><td>${m.createdAt ? fmtLocal(m.createdAt) : '—'}</td><td>${esc(typeLabel(m.document_type))}</td><td>${esc(m.document_number || '—')}</td><td>${esc(m.account_name || '—')}</td><td class="r" style="color:${ch > 0 ? '#16a34a' : ch < 0 ? '#dc2626' : '#6b7280'}">${ch > 0 ? '+' : ''}${ch}</td><td class="r">${m.quantity_before ?? '—'}</td><td class="r">${m.quantity_after ?? '—'}</td></tr>`;
                }).join('');
                const win = window.open('', '_blank');
                if (!win) { alert('Pop-up blocked — allow pop-ups to print.'); return; }
                win.document.write(`<html><head><title>Product History</title><style>body{font-family:Arial,sans-serif;font-size:12px;padding:24px;}h2{margin-bottom:4px;}p{color:#666;margin-top:0;}table{width:100%;border-collapse:collapse;margin-top:12px;}th,td{padding:6px 9px;border-bottom:1px solid #eee;text-align:left;}th{background:#f3f4f6;font-size:10px;text-transform:uppercase;}td.r,th.r{text-align:right;}</style></head><body><h2>Product History — ${esc(selectedProduct?.name || '')}</h2><p>Current stock: ${stats.current} · Total in: +${stats.totalIn} · Total out: -${stats.totalOut} · ${filtered.length} movements</p><table><thead><tr><th>Date</th><th>Type</th><th>Document</th><th>Account</th><th class="r">Change</th><th class="r">Before</th><th class="r">After</th></tr></thead><tbody>${rows}</tbody></table></body></html>`);
                win.document.close(); win.focus(); win.print();
              }}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg"
            >
              Print
            </button>
          </>
        )}
      </div>

      {/* Empty state */}
      {!selectedProductId && (
        <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400 text-sm">
          Select a product to view its movement history.
        </div>
      )}

      {selectedProductId && (
        <>
          {/* Stats bar */}
          {!isLoading && movements.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
              {[
                { label: 'Current Stock', value: stats.current,  color: GREEN },
                { label: 'Total In',      value: `+${stats.totalIn}`,  color: '#16a34a' },
                { label: 'Total Out',     value: `-${stats.totalOut}`, color: '#dc2626' },
                { label: 'Movements',     value: stats.count,    color: '#6b7280' },
              ].map(c => (
                <div key={c.label} style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: '12px 16px', borderLeft: `3px solid ${c.color}` }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{c.label}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#111' }}>{c.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Count */}
          {!isLoading && (
            <div className="text-xs text-gray-400 mb-3">
              {filtered.length.toLocaleString()} records
              {totalPages > 1 && ` · page ${page} of ${totalPages}`}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-10 text-center text-gray-400 text-sm">No movements found.</div>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Type</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Document</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Account</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Variant</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Change</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Before</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">After</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((m, idx) => {
                    const typeInfo = DOC_TYPE_LABELS[m.document_type] || { label: m.document_type, bg: '#f3f4f6', color: '#374151' };
                    const change   = m.quantity_change ?? 0;
                    const isIn     = change > 0;
                    const isOut    = change < 0;

                    return (
                      <tr
                        key={m._id || idx}
                        className="border-b border-gray-100 last:border-0 hover:bg-gray-50"
                      >
                        <td className="px-4 py-2.5 text-gray-500 text-xs whitespace-nowrap">
                          {m.createdAt ? fmtLocal(m.createdAt) : '—'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span style={{ background: typeInfo.bg, color: typeInfo.color, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                            {typeInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          {m.document_number ? (
                            <button
                              onClick={() => navigate(`/documents/${m.document_id}`)}
                              className="font-mono text-xs font-semibold hover:underline"
                              style={{ color: GREEN, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                              {m.document_number}
                            </button>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-700 text-sm">
                          {m.account_name || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-gray-500 text-xs">
                          {m.variant_name || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold">
                          <span style={{ color: isIn ? '#16a34a' : isOut ? '#dc2626' : '#6b7280' }}>
                            {isIn ? '+' : ''}{change}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-gray-500 text-xs">
                          {m.quantity_before ?? '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-gray-800">
                          {m.quantity_after ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
              <span>{((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length.toLocaleString()}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium">
                  ← Prev
                </button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 font-medium">
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
